import styled, { css, keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import {
	handleColorType,
	media,
	mediaQueries,
	disableUserSelect,
	growTransition,
} from '@quizzach/styles';

/* the redesign page background — glass surfaces mix against it */
const designBg = ({ theme }: { theme: any }) => theme.design?.bg ?? '#191b1d';

/* Author emphasis inside quiz text (u/b from the editor HTML): a gold
   highlighter swipe at 70% opacity under the phrase + brand-teal bold.
   The swipe is one SVG pill: ry=50% keeps the caps circular at any
   stretch (rx follows ry per SVG2 auto), so the translucent band has no
   cap/band seams that a multi-layer gradient pill would show. */
const swipePill = ({ theme }: { theme: any }) => {
	const gold = (theme.design?.gold ?? '#DBB154').replace('#', '%23');
	return `url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg'><rect width='100%25' height='100%25' ry='50%25' fill='${gold}' fill-opacity='0.7'/></svg>")`;
};

export const richTextEmphasis = css`
	u {
		text-decoration: none;
		background-image: ${swipePill};
		background-repeat: no-repeat;
		background-size: 100% 0.2em;
		/* tucked just under the word — descender tails may graze the band */
		background-position: center bottom 0.05em;
		box-decoration-break: clone;
		-webkit-box-decoration-break: clone;
	}

	b,
	strong {
		font-weight: 700;
		/* gold, not brand teal — teal stays reserved for answers */
		color: ${({ theme }) => theme.design?.goldInk ?? '#DBB154'};
	}
`;

const threadsDots = keyframes`
	from {
		background-position: 0 0, 0 0, 0 0, 0 0, 0 0;
	}
	to {
		background-position: 14px 9px, -12px 16px, 20px -8px, -16px -12px, 10px -14px;
	}
`;

/* channel-tuning: refreshed TV-code digits arrive through a short blur */
const tuneInDigits = keyframes`
	from {
		opacity: 0;
		filter: blur(6px);
	}
	60% {
		opacity: 1;
		filter: blur(2.5px);
	}
	to {
		opacity: 1;
		filter: blur(0);
	}
`;

export const QuizContainer = styled.div`
	/* content stacks above the absolutely-positioned FramedCover backdrop */
	position: relative;
	z-index: 1;

	/* top padding clears the fixed frosted TopNavBar of the redesign shell */
	${mediaQueries.mediaWidth} {
		padding: 84px 10px 10px;
		display: flex;
		flex-direction: column;
		flex: 1;
	}

	${mediaQueries.desktopWidth} {
		padding: 96px 20px 120px;
		max-width: 1900px;
		width: 100%;
		margin: 0px auto;
		min-height: 100vh;
		display: flex;
		flex-direction: column;
	}

	.eye-icon {
		position: absolute;
		right: 10px;
		top: 50%;
		transform: translateY(-50%);
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
	}
	.validation-alert {
		margin: 0;
		padding: 0;
		background-color: transparent;
		gap: 5px;

		.alert-icon {
			svg {
				width: 15px;
			}
		}
	}
`;

export const QuizSlidesContainer = styled.div`
	/* clears the fixed mobile arrow bar (~65px + safe area) */
	padding-bottom: 84px;

	/* handwritten gold whisper above the chapter title (»poglavlje« on intro,
	   »kraj poglavlja« on end slides); break slides carry the same whisper
	   in coral above their title */
	.chapter-end-label,
	.break-hand-label {
		display: block;
		font-family: ${({ theme }) => theme.typography.fontHandwritten};
		font-weight: 600;
		font-size: 32px;
		line-height: 1;
		text-transform: lowercase;
		color: ${({ theme }) => theme.design?.gold ?? '#DBB154'};
		transform: rotate(-1.4deg);
		margin-bottom: 10px;
	}

	.break-hand-label {
		font-size: 26px;
		margin-bottom: 0;
	}

	/* inline gold handwritten »primjer« inside chapter/break copy —
	   Caveat runs optically small, so the word gets an em bump */
	.hand-gold-word {
		font-family: ${({ theme }) => theme.typography.fontHandwritten};
		font-weight: 600;
		font-size: 1.35em;
		line-height: 1;
		color: ${({ theme }) => theme.design?.gold ?? '#DBB154'};
		display: inline-block;
		transform: rotate(-1.4deg);
	}

	${media.smallDesktop} {
		.chapter-end-label {
			font-size: 51px;
		}

		.break-hand-label {
			font-size: 40px;
		}
	}

	.blurryQ {
		min-width: 300px;
		max-height: 80vh;
	}

	${richTextEmphasis}

	.loading-skeleton {
		border-radius: 16px;
		background: ${({ theme }) =>
			`linear-gradient(-90deg, ${theme.palette.quiz.surfaceMain} 0%, ${theme.palette.quiz.surfaceSoft}  50%, ${theme.palette.quiz.surfaceMain}  100%)`};
		background-size: 400% 400%;
		animation: loadingPulse 1.2s ease-in-out infinite;
		line-height: 1;
		max-height: 550px;
		max-width: 800px;
		width: 100%;
		height: 100%;
	}
	.img-loading-skeleton {
		border-radius: 16px;
		background: ${({ theme }) =>
			`linear-gradient(-90deg, ${theme.palette.quiz.surfaceMain} 0%, ${theme.palette.quiz.surfaceSoft}  50%, ${theme.palette.quiz.surfaceMain}  100%)`};
		background-size: 400% 400%;
		animation: loadingPulse 1.2s ease-in-out infinite;
		line-height: 1;
		height: 250px;
		width: 100%;
	}

	flex: 1;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	position: relative;
	z-index: 2;
	text-align: left;

	/* free space splits 2:3 around the slide so short questions start
	   ~2/5 down the backdrop; basis-0 + shrink collapses both spacers
	   once a tall slide fills the viewport, falling back to normal
	   top flow */
	&::before,
	&::after {
		content: '';
		display: block;
	}

	&::before {
		flex: 2 1 0;
	}

	&::after {
		flex: 3 1 0;
	}

	/* chapter and break slides sit dead-center instead of the lower-third stage */
	&.slides-centered::before,
	&.slides-centered::after {
		flex: 1 1 0;
	}

	.quiz-break-content {
		display: flex;
		flex-direction: column;
		align-items: center;
		margin: 0px auto;
		text-align: center;

		/* editor HTML may mark alignment with a class instead of an
		   inline style — honor it against the centered default */
		.align-left {
			text-align: left;
		}
		max-width: 600px;
		/* mobile question views pad 10px inside the shell (QuestionContainer)
		   — break/chapter slides follow; the desktop block below resets it */
		padding: 0px 10px;
		box-sizing: border-box;

		button {
			margin: 30px 0px;
		}

		.quiz-break-main {
			margin: 20px 0px;
			/* span the slide so the editor's inline text-align (left/right)
			   has real width to align against instead of a shrink-wrapped box */
			width: 100%;

			h3 {
				margin: 20px 0px;
			}

			/* keep the coral whisper tucked against its title */
			.break-hand-label + h3 {
				margin-top: 6px;
			}

			.chapter-description {
				/* CKEditor emits no text-align for left (its default), so default to
				   left here; center/right arrive as explicit inline styles */
				text-align: left;

				b {
					font-weight: 700;
				}

				/* editor HTML arrives with inline font sizes on its tags —
				   the slide's own scale must win for presentation sizing
				   (the handwritten »primjer« keeps its own em bump) */
				*:not(.hand-gold-word) {
					font-size: inherit !important;
					line-height: inherit !important;
				}
			}
		}
		.quiz-break-feedback {
			margin: 20px 0px;

			h4 {
				margin: 0px;
			}
		}
	}

	.background-qupitnik {
		position: absolute;
		z-index: 1;
		right: 20px;
		top: 30px;
	}
	.flex {
		display: flex;
	}
	.flex-stretch {
		flex: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: space-between;
		margin: 0px auto;
	}

	.question-slides-next {
		display: flex;
		justify-content: flex-end;
		align-items: flex-end;
	}

	.bold {
		font-weight: 500;
	}
	.marginR {
		margin-right: 10px;
	}
	.marginT {
		margin-top: 10px;
	}
	.marginL {
		margin-left: 10px;
	}
	.marginB {
		margin-bottom: 10px;
	}

	.img-wrapper {
		max-width: 100%;
		/* Fixed, viewport-derived box: the slide's height must not depend on
		   when (or whether) the image loads, or the fit-to-viewport scale
		   visibly jumps mid-slide. The image centers inside the reserve.
		   The viewport term is multiplied by --fit-zoom so that when the
		   slide overflows, the image reserve gives up height FASTER than the
		   text shrinks — the fit solver then lands on a near-1 scale and the
		   question text keeps its size while only the image scales down. */
		height: clamp(
			220px,
			calc((100vh - 120px) * var(--fit-zoom, 1) * var(--fit-zoom, 1)),
			350px
		);
		text-align: center;
		margin-bottom: 30px;
		z-index: 4;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;

		.img-loading-skeleton,
		.loading-skeleton {
			height: 100%;
			max-height: 100%;
		}

		figure {
			max-height: 100%;
			margin: 0px;
		}

		img {
			max-width: 100%;
			max-height: clamp(
				220px,
				calc((100vh - 120px) * var(--fit-zoom, 1) * var(--fit-zoom, 1)),
				350px
			);
			border-radius: 16px;
			margin: 0px auto;
			display: block;
		}
		.answer {
			max-height: 400px;
		}

		/* Caption row under the photo: credits left, expand action right.
		   The row gets the photo's measured width inline, so the auto
		   margins keep it centered under the (also centered) photo. */
		.img-meta {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 10px;
			margin: 8px auto 0px;
		}

		.img-credits {
			width: max-content;
			margin-top: 0px;
			padding: 2px 10px;
			border-radius: 16px;
			background-color: ${({ theme }) => theme.palette.quiz.surfaceTransparent};
		}

		/* Text color and size come from the same Text/xxs/surfaceNeutral combo
		   the credits use, so both caption halves read identically. */
		.img-expand-action {
			margin-left: auto;
			display: inline-flex;
			align-items: center;
			gap: 6px;
			width: max-content;
			padding: 2px 10px;
			border-radius: 16px;
			background-color: ${({ theme }) => theme.palette.quiz.surfaceTransparent};
			white-space: nowrap;
			cursor: pointer;
		}
	}

	.question-slides-container-intro {
		display: flex;
		flex-direction: column;

		button {
			margin: 30px auto;
		}
	}

	.intro-container {
		.start-button {
			margin: 20px auto 0px auto;
		}
	}

	${media.smallDesktop} {
		padding: 0px 0px 20px;

		.quiz-break-content {
			max-width: 1000px;
			padding: 0px;

			.quiz-break-main {
				margin: 0px 0px 40px 0px;
				h3 {
					margin: 30px 0px;
					font-size: 48px;
				}

				p,
				.chapter-description {
					font-size: 25px;
				}
			}
			.quiz-break-feedback {
				margin: 40px 0px;

				h4 {
					margin: 10px 0px;
					font-size: 25px;
				}

				p {
					font-size: 22px;
				}
			}
		}
	}

	${media.largeDesktop} {
		.quiz-break-content {
			.quiz-break-main {
				h3 {
					font-size: 53px;
				}

				p,
				.chapter-description {
					font-size: 28px;
				}
			}

			.quiz-break-feedback {
				h4 {
					font-size: 28px;
				}

				p {
					font-size: 23px;
				}
			}
		}
	}

	${media.ultrawide} {
		.chapter-end-label {
			font-size: 58px;
		}

		.quiz-break-content {
			max-width: 1300px;

			.quiz-break-main {
				h3 {
					font-size: 61px;
				}

				p,
				.chapter-description {
					font-size: 32px;
				}
			}

			.quiz-break-feedback {
				h4 {
					font-size: 32px;
				}

				p {
					font-size: 25px;
				}
			}
		}
	}

	${media.smallDesktop} {
		.background-qupitnik {
			position: absolute;
			z-index: 1;
			right: 50px;
			top: 0px;
		}

		.visual {
			display: flex;
			gap: 40px;
			z-index: 2;
			align-items: flex-end;
		}
		/* Keeps the image clear of the slide's top edge when the panel is the
		   row's tallest element (via min-height on a short text column). */
		.visual-answer .img-wrapper {
			margin-top: 24px;
		}
		.visual-text {
			flex: 1 1 48%;
			min-width: 0;
			/* same 2:3 stage split as the slide container: when the image panel
			   makes the row taller than the text, the text starts ~2/5 down the
			   column instead of hugging the bottom edge */
			align-self: stretch;
			display: flex;
			flex-direction: column;

			&::before,
			&::after {
				content: '';
				display: block;
			}

			&::before {
				flex: 2 1 0;
			}

			&::after {
				flex: 3 1 0;
			}
		}
		.img-wrapper {
			flex-basis: 52%;
			/* The panel stretches to the height the TEXT column gives the row,
			   so the image uses all the vertical space actually available
			   beside the question instead of a fixed viewport-derived box.
			   The figure is position:absolute (below) so the image contributes
			   nothing back to the row height — slide height stays a pure
			   function of the text, never of when (or whether) the image
			   loads, and the fit solver cannot oscillate through it. The
			   min-height keeps the old stable reserve as a floor for visual
			   questions whose text column is short: viewport minus the play
			   chrome (~320px of nav, navigator and paddings) and this
			   wrapper's own 40px bottom margin, with the --fit-zoom multiplier
			   making that reserve absorb slide overflow ahead of the text
			   (see the base .img-wrapper note). */
			align-self: stretch;
			position: relative;
			height: auto;
			min-height: clamp(
				220px,
				calc((100vh - 360px) * var(--fit-zoom, 1) * var(--fit-zoom, 1)),
				650px
			);
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			margin-bottom: 40px;

			figure {
				position: absolute;
				inset: 0;
				margin: 0px;
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-content: center;
			}
			/* Pre-measure fallback only: the caption row normally carries the
			   photo's measured width inline (aligning it with the photo's
			   edges); until that lands, stretch to the panel width. */
			.img-meta {
				align-self: stretch;
			}
			.img-credits {
				width: max-content;
				margin-top: 0px;
				padding: 2px 10px;
				border-radius: 16px;
				background-color: ${({ theme }) =>
					theme.palette.quiz.surfaceTransparent};
			}
			img {
				max-width: 100%;
				/* The absolutely positioned figure spans the stretched wrapper,
				   so 100% here means "the full height of the panel". */
				max-height: 100%;
				display: block;
				position: relative;
			}
		}

		.flex {
			justify-content: flex-start;
			align-items: center;
		}
		.flex-small {
			display: flex;
			justify-content: flex-start;
			align-items: flex-start;
			flex-direction: column;
			margin-right: 50px;
		}

		.question-slides-next {
			margin-top: auto;
			margin-bottom: 20px;
		}

		.bold {
			font-weight: 600;
		}
		.marginR {
			margin-right: 20px;
		}
		.marginT {
			margin-top: 20px;
		}
		.marginL {
			margin-left: 20px;
		}
		.marginB {
			margin-bottom: 20px;
		}
	}
`;

/* Glass band over the framed cover (same language as the answer
   RevealBand): translucent panel + blur keeps hairlines and text legible
   on any photo. Shared by the /play and mode rows and the start SpecBar. */
const glassBand = css`
	background: ${({ theme }) =>
		theme.mode === 'light'
			? `color-mix(in srgb, ${
					theme.design?.panel2 ?? '#fff9ee'
				} 78%, transparent)`
			: `color-mix(in srgb, ${
					theme.design?.bg ?? '#191b1d'
				} 55%, transparent)`};
	backdrop-filter: blur(8px);
	-webkit-backdrop-filter: blur(8px);

	/* blur is a GPU tax on phones; the translucent fill alone carries it */
	@media (max-width: 1079px) {
		backdrop-filter: none;
		-webkit-backdrop-filter: none;
	}
`;

export const QuizIntroContainer = styled.div<{ $noBottomBar?: boolean }>`
	cursor: default;
	/* clears the fixed bottom start bar (~65px + safe area) — the TV-code and
	   mode screens carry no such bar, so they keep the space */
	padding-bottom: ${({ $noBottomBar }) => ($noBottomBar ? '0' : '84px')};
	display: flex;
	flex-direction: column;
	flex: 1;

	.flex {
		display: flex;
		flex-wrap: wrap;
	}
	h4 {
		margin: 0px 2px;
	}

	.tooltip {
		position: relative;
		display: flex;
		align-items: center;
	}

	.tooltip .tooltiptext {
		visibility: hidden;
		width: 200px;
		background-color: ${({ theme }) => theme.palette.quiz.surfaceSoft};
		text-align: center;
		border-radius: 6px;
		padding: 5px 10px;

		position: absolute;
		z-index: 6;
		left: 100%;
		top: 50%;
		transform: translateY(-50%);
		margin-right: 5px;
		text-align: left;
	}

	.tooltip:hover .tooltiptext {
		visibility: visible;
	}

	/* Mode picker as full-width hairline list rows (homepage FRow look). */
	/* Mobile: homepage hand-note scale (SecHead .hand), left-aligned;
	   desktop restores the larger centered title below. The auto margin on
	   the wrap drops the title into the framed cover, right above the rows
	   — same lower-third seat as the quiz-start greeting. */
	.quiz-mode-titlewrap {
		margin-top: auto;
	}

	.quiz-mode-title {
		font-family: ${({ theme }) => theme.typography.fontHandwritten};
		font-weight: 600;
		text-transform: none;
		font-size: clamp(23px, 2.2vw, 31px);
		line-height: 1.1;
		color: var(--coral);
		/* centred at every width: on a phone the note sits over the cover with
		   the rows beneath it, and a left-hugging line read as a stray caption
		   rather than the screen's greeting */
		text-align: center;
		/* the question views' 10px shell gutter (see .quiz-start-head) — also
		   keeps Caveat's overhanging first glyph off the screen edge; the 9px
		   on top drops the note a hair further onto the cover (desktop resets
		   the padding and keeps its own margins) */
		padding: 9px 10px 0;
		transform: rotate(-1deg);
	}

	.quiz-mode-rows {
		margin-top: 18px;
		text-align: left;
	}

	/* Each row is itself a Reveal element (homepage Formats animation) and
	   breaks out of the content column: the negative vw margins stretch the
	   hairlines, hover wash and click area to the full viewport width, while
	   the matching padding keeps the content aligned to the column. The vw
	   terms divide by --fit-zoom so the bleed still reaches the real viewport
	   edges when the desktop FitToViewport shrinks the screen (zoom leaves vw
	   unscaled; the var is 1 outside a fitted slide). */
	.quiz-mode-row {
		margin-left: calc(50% - 50vw / var(--fit-zoom, 1));
		margin-right: calc(50% - 50vw / var(--fit-zoom, 1));
		padding-left: calc(50vw / var(--fit-zoom, 1) - 50%);
		padding-right: calc(50vw / var(--fit-zoom, 1) - 50%);
		cursor: pointer;
		transition: background 0.25s;

		/* Phones drop the hairlines: the rows read straight off the cover
		   photo, so the type carries its own contrast instead — a tight halo
		   plus a wider, softer one in the page ink, invisible over the dark
		   page but enough to hold the letterforms over a bright patch of
		   photo. Desktop puts the rules back (see the smallDesktop block). */
		text-shadow:
			0 0 4px ${({ theme }) => theme.design?.bg ?? '#191b1d'},
			0 1px 14px ${({ theme }) => theme.design?.bg ?? '#191b1d'};

		&:hover {
			background: color-mix(
				in srgb,
				${({ theme }) => theme.palette.quiz.textMain} 4%,
				transparent
			);
		}

		/* Mobile: single column — number above title, description below,
		   arrow on its own row at the end. Desktop rearranges into one row. */
		.in {
			display: grid;
			grid-template-columns: 1fr;
			align-items: center;
			justify-items: start;
			gap: 12px 14px;
			padding: 24px 6px;
		}

		.num {
			font-family: var(--fd);
			font-size: 14px;
			color: ${({ theme }) => theme.palette.quiz.textMuted};
			letter-spacing: 1px;
		}

		/* The sequence measures itself against this box, so it must track the
		   grid column instead of growing with the nowrap text. */
		.main {
			justify-self: stretch;
			min-width: 0;
			max-width: 100%;
		}

		.main h3 {
			font-family: var(--fd);
			font-weight: 400;
			text-transform: uppercase;
			letter-spacing: 0.2px;
			line-height: 1.05;
			margin: 0;
			/* Never wraps: JS in ModeSequence shrinks the font to fit the row. */
			white-space: nowrap;
			font-size: clamp(22px, 5.6vw, 52px);
			color: ${({ theme }) => theme.palette.quiz.textMain};

			.ans {
				color: ${({ theme }) => theme.palette.quiz.textBrand};
			}

			.sep {
				color: ${({ theme }) => theme.palette.quiz.textMuted};
				margin: 0 0.14em;
			}
		}

		.desc {
			font-size: 14.5px;
			line-height: 1.5;
			color: ${({ theme }) => theme.palette.quiz.textSoft};
			max-width: 560px;
		}

		.desc.note {
			color: ${({ theme }) => theme.palette.quiz.textBrand};
		}

		.arr {
			justify-self: end;
			width: 44px;
			height: 44px;
			border: 1.5px solid
				color-mix(
					in srgb,
					${({ theme }) => theme.palette.quiz.textMain} 30%,
					transparent
				);
			border-radius: 50%;
			display: flex;
			align-items: center;
			justify-content: center;
			transition: 0.3s;
			flex-shrink: 0;
			color: ${({ theme }) => theme.palette.quiz.textMain};
		}

		&:hover .arr {
			background: ${({ theme }) => theme.palette.quiz.brandMain};
			border-color: ${({ theme }) => theme.palette.quiz.brandMain};
			/* dark arrow on the teal fill, same as homepage rows */
			color: var(--teal-deep);
			transform: rotate(-45deg);
		}

		.quiz-mode-halftime {
			margin-top: 14px;
			display: flex;
			align-items: center;
			gap: 10px;
			cursor: pointer;
			width: fit-content;
		}

		.quiz-mode-switch {
			width: 42px;
			height: 24px;
			border-radius: var(--r-pill);
			background: rgba(var(--ink-rgb), 0.15);
			position: relative;
			flex-shrink: 0;
			transition: background 0.2s;

			&::after {
				content: '';
				position: absolute;
				top: 3px;
				left: 3px;
				width: 18px;
				height: 18px;
				border-radius: 50%;
				background: var(--panel-2);
				transition:
					transform 0.2s,
					background 0.2s;
			}

			&.on {
				background: var(--teal);
			}

			&.on::after {
				transform: translateX(18px);
				background: var(--teal-deep);
			}
		}

		.quiz-mode-halftime-label {
			font-size: 15px;
			color: ${({ theme }) => theme.palette.quiz.textMain};
		}

		&.disabled {
			cursor: default;

			&:hover {
				background: none;
			}

			&:hover .arr {
				background: none;
				border-color: color-mix(
					in srgb,
					${({ theme }) => theme.palette.quiz.textMain} 30%,
					transparent
				);
				color: ${({ theme }) => theme.palette.quiz.textMain};
				transform: none;
			}

			.num,
			.main,
			.arr {
				opacity: 0.35;
			}
		}
	}

	.quiz-mode-template {
		margin-top: 26px;
		display: flex;
		justify-content: flex-start;
	}

	/* /play (TV code) reuses the mode-row grammar: the TV row is
	   informational — no hover wash, no pointer — only the device row acts. */
	.quiz-mode-row.static {
		cursor: default;

		&:hover {
			background: none;
		}
	}

	/* Over the framed cover each /play row becomes a glass band (same
	   language as the answer RevealBand) so the hairlines and descriptions
	   stay legible on any photo; the actionable row's hover tints the
	   glass instead of replacing it. */
	.quiz-play-rows .quiz-mode-row,
	.quiz-play-rows .quiz-mode-row.static:hover,
	.quiz-play-rows .quiz-mode-row.disabled:hover {
		${glassBand}
	}

	.quiz-play-rows .quiz-mode-row:not(.static):not(.disabled):hover {
		background: ${({ theme }) =>
			theme.mode === 'light'
				? `color-mix(in srgb, ${
						theme.palette.quiz.textMain
					} 6%, color-mix(in srgb, ${
						theme.design?.panel2 ?? '#fff9ee'
					} 78%, transparent))`
				: `color-mix(in srgb, ${
						theme.palette.quiz.textMain
					} 6%, color-mix(in srgb, ${
						theme.design?.bg ?? '#191b1d'
					} 55%, transparent))`};
	}

	/* Handwritten »novo!« kick above the TV row title, same hand-note
	   grammar as the profile fresh-note. */
	.quiz-play-new {
		display: block;
		width: max-content;
		font-family: var(--fh);
		font-weight: 600;
		font-size: 22px;
		line-height: 1;
		color: var(--gold);
		transform: rotate(-2deg);
		margin-bottom: 8px;
	}

	/* Code + its »TV app kod« caption travel as one block: below the
	   description on mobile (order jumps it past .desc), in the far-right
	   column on desktop. */
	/* The trigger pins to the left edge, the code to the right; flex-end
	   drops »saznaj više« onto the same line as the »TV app kod« caption
	   under the digits. */
	.quiz-play-code-block {
		order: 1;
		display: flex;
		align-items: flex-end;
		justify-content: space-between;
		gap: 20px;
		width: 100%;
	}

	.quiz-play-code-col {
		display: flex;
		flex-direction: column;
		align-items: center;
	}

	/* »saznaj više« with a hairline-circled ? — sits left of the code and
	   opens the TV-app info dialog. */
	.quiz-play-more {
		display: flex;
		align-items: center;
		gap: 8px;
		background: none;
		border: none;
		padding: 0;
		font: inherit;
		font-size: 14px;
		color: ${({ theme }) => theme.palette.quiz.textSoft};
		cursor: pointer;
		transition: color 0.2s;

		.q {
			width: 22px;
			height: 22px;
			flex-shrink: 0;
			border: 1.5px solid
				color-mix(
					in srgb,
					${({ theme }) => theme.palette.quiz.textMain} 30%,
					transparent
				);
			border-radius: 50%;
			display: flex;
			align-items: center;
			justify-content: center;
			color: ${({ theme }) => theme.palette.quiz.textMain};
			transition: 0.2s;

			/* the Q-logo glyph follows the circle's color (hover included) */
			svg path {
				fill: currentColor;
			}
		}

		&:hover {
			color: ${({ theme }) => theme.palette.quiz.textMain};
		}

		&:hover .q {
			background: ${({ theme }) => theme.palette.quiz.brandMain};
			border-color: ${({ theme }) => theme.palette.quiz.brandMain};
			color: var(--teal-deep);
		}

		/* desktop variant (below the description) waits for its breakpoint */
		&.desk {
			display: none;
		}
	}

	/* Mobile: the framed cover sits 14px off the viewport edge, so the row
	   content backs off a little further than the shared 10px gutter. */
	@media (max-width: 1079px) {
		.quiz-play-rows .quiz-mode-row .in {
			padding-left: 20px;
			padding-right: 20px;
		}

		/* The 4:1 spacers (.quiz-start-space) now seat the play/mode rows in
		   the lower third, so the title no longer needs the auto margin that
		   would shove the block flush against the bottom. */
		.quiz-mode-titlewrap {
			margin-top: 0;
		}

		/* Phones drop the glass band too, so the /play rows read off the
		   cover exactly like the mode rows. */
		.quiz-start-row,
		.quiz-play-rows .quiz-mode-row,
		.quiz-play-rows .quiz-mode-row:hover,
		.quiz-play-rows .quiz-mode-row.static:hover,
		.quiz-play-rows .quiz-mode-row.disabled:hover {
			background: none;
			backdrop-filter: none;
			-webkit-backdrop-filter: none;
		}

		/* The start rows lose their band here too (the mode rows carry the
		   same halo on phones, see .quiz-mode-row). */
		.quiz-start-row {
			text-shadow:
				0 0 4px ${({ theme }) => theme.design?.bg ?? '#191b1d'},
				0 1px 14px ${({ theme }) => theme.design?.bg ?? '#191b1d'};
		}

		/* The descriptions ran softest of all — over a photo they need the
		   main ink, and the arrow ring a firmer hairline. */
		.quiz-mode-row .desc {
			color: ${({ theme }) => theme.palette.quiz.textMain};
		}

		.quiz-mode-row .arr {
			border-color: color-mix(
				in srgb,
				${({ theme }) => theme.palette.quiz.textMain} 55%,
				transparent
			);
		}
	}

	/* Same kicker grammar as the answer-band »odgovor« label. */
	.quiz-play-code-label {
		font-family: ${({ theme }) => theme.typography.fontSecondary};
		font-weight: 700;
		font-size: 12.5px;
		letter-spacing: 2.4px;
		text-transform: uppercase;
		color: ${({ theme }) => theme.palette.quiz.textMuted};
	}

	.quiz-play-code {
		display: flex;
		align-items: center;
		height: 52px;

		/* digits tune in staggered left to right, like picking up a channel;
		   the swap re-runs it because loading unmounts the spans in between */
		&.show span {
			opacity: 0;
			animation: ${tuneInDigits} 320ms ease-out forwards;
		}

		&.show span:nth-child(2) {
			animation-delay: 60ms;
		}

		&.show span:nth-child(3) {
			animation-delay: 120ms;
		}

		&.show span:nth-child(4) {
			animation-delay: 180ms;
		}

		span {
			font-family: var(--fd);
			font-size: clamp(38px, 9vw, 46px);
			line-height: 1;
			letter-spacing: 0.14em;
			color: ${({ theme }) => theme.palette.quiz.textBrand};
		}

		.quiz-play-code-dots {
			width: 150px;
			height: 44px;
			border-radius: 22px;
			background-image: ${({ theme }) => `
				radial-gradient(circle at 14% 22%, ${theme.palette.quiz.textMain}55 1.2px, transparent 1.9px),
				radial-gradient(circle at 68% 58%, ${theme.palette.quiz.textMain}66 1.6px, transparent 2.5px),
				radial-gradient(circle at 46% 84%, ${theme.palette.quiz.textMain}83 0.9px, transparent 2.1px),
				radial-gradient(circle at 90% 16%, ${theme.palette.quiz.textMain}8d 1.4px, transparent 2.6px),
				radial-gradient(circle at 28% 47%, ${theme.palette.quiz.textMain}80 0.8px, transparent 1.7px),
				radial-gradient(circle at 78% 36%, ${theme.palette.quiz.textMain}85 1.8px, transparent 2.8px),
				radial-gradient(circle at 12% 70%, ${theme.palette.quiz.textMain}83 1.5px, transparent 2.4px)
			`};
			background-size:
				13px 15px,
				22px 23px,
				25px 19px,
				30px 28px,
				18px 20px,
				26px 22px,
				20px 26px;
			background-repeat: repeat;
			filter: blur(0.28px);
			animation: ${threadsDots} 2.1s linear infinite;
		}
	}

	/* Quiz start (intro) screen in the premade-detail anatomy: coral hand
	   greeting + big Fjalla title with the author signing the right hand-slot
	   on desktop, the cover on the shared FramedCover backdrop (same as the
	   slides), and the specs as ONE horizontal SpecBar on desktop that falls
	   back to stacked full-bleed hairline rows on mobile. */
	.quiz-start-head {
		display: flex;
		align-items: flex-end;
		justify-content: space-between;
		gap: 20px;
		text-align: left;
		/* mobile question views pad 10px inside the shell (QuestionContainer)
		   — the intro column follows; the desktop gutter replaces it below */
		padding: 0 10px;
	}

	/* Lower-third stage with breathing room, same as QuizSlidesContainer:
	   free space splits 4:1 around the content so it floats ~20% above the
	   bottom; basis-0 + shrink collapses both spacers once content fills
	   the viewport. */
	.quiz-start-space {
		display: block;
		flex: 4 1 0;
	}

	.quiz-start-space.bottom {
		flex: 1 1 0;
	}

	.quiz-start-headmain {
		flex: 1 1 auto;
		min-width: 0;
	}

	.quiz-start-author {
		display: none;
		flex: 0 0 auto;
		align-items: center;
		transform: rotate(-1.6deg);
		padding-bottom: 10px;

		.author-img img {
			height: 30px;
			display: block;
		}

		.author-img.sq img {
			height: 36px;
		}

		.author-img .v-neon {
			display: none;
		}

		.author-hand {
			font-family: ${({ theme }) => theme.typography.fontHandwritten};
			font-weight: 600;
			font-size: clamp(26px, 2vw, 32px);
			color: ${({ theme }) => theme.palette.quiz.textBrand};
		}
	}

	.quiz-start-greet {
		display: inline-block;
		font-family: ${({ theme }) => theme.typography.fontHandwritten};
		font-weight: 600;
		font-size: clamp(27px, 2.7vw, 38px);
		color: var(--coral);
		transform: rotate(-1deg);
		text-align: left;
		margin-top: 10px;
	}

	.quiz-start-title {
		font-size: clamp(27px, 3vw, 44px);
		line-height: 1.05;
		color: ${({ theme }) => theme.palette.quiz.textMain};
		text-align: left;
		margin: 6px 0 0;
	}

	.quiz-start-rows {
		margin-top: 20px;
		text-align: left;
	}

	.quiz-start-row {
		margin-left: calc(50% - 50vw / var(--fit-zoom, 1));
		margin-right: calc(50% - 50vw / var(--fit-zoom, 1));
		padding: 15px calc(50vw / var(--fit-zoom, 1) - 50% + 10px);
		border-top: 1px solid
			color-mix(
				in srgb,
				${({ theme }) => theme.palette.quiz.textMain} 15%,
				transparent
			);
		display: grid;
		grid-template-columns: 1fr;
		gap: 5px;
		/* mobile spec rows sit on the framed cover — same glass as /play */
		${glassBand}

		&:last-of-type {
			border-bottom: 1px solid
				color-mix(
					in srgb,
					${({ theme }) => theme.palette.quiz.textMain} 15%,
					transparent
				);
		}
	}

	.quiz-start-lab {
		font-size: 13px;
		font-weight: 700;
		letter-spacing: 2.2px;
		text-transform: uppercase;
		color: ${({ theme }) => theme.palette.quiz.textMuted};
	}

	.quiz-start-val {
		font-family: var(--fd);
		font-size: clamp(19px, 1.8vw, 26px);
		text-transform: uppercase;
		letter-spacing: 0.3px;
		line-height: 1.2;
		color: ${({ theme }) => theme.palette.quiz.textMain};

		.ans {
			color: ${({ theme }) => theme.palette.quiz.textBrand};
		}

		.sep {
			color: ${({ theme }) => theme.palette.quiz.textMuted};
			margin: 0 0.12em;
		}
	}

	.info-container {
		margin-top: 30px;

		button {
			max-width: 300px;
			width: 100%;
		}

		.info {
			border-radius: 35px;
			background-color: ${({ theme }) => theme.palette.quiz.surfaceTransparent};
			padding: 20px;
			text-align: left;
			margin: 10px 0px;
			& > :nth-child(1) {
				font-size: 15px;
			}
			& > :nth-child(2) {
				padding: 15px 0px 0px;
			}
		}

		.info-author-name {
			font-size: 25px;
			line-height: 115%;
		}
	}
	.logo-nogac {
		width: 50px;
	}

	.logo-vragolasti {
		width: 110px;
	}
	${media.tablet} {
		.info-container {
			.info {
				display: flex;
				align-items: center;
				justify-content: flex-start;

				& > :nth-child(1) {
					max-width: 100px;
					width: 100%;
					box-sizing: border-box;
					font-size: 20px;
					text-align: right;
				}
				& > :nth-child(2) {
					padding: 20px;
					flex-basis: 80%;
				}
			}

			.info-author-name {
				font-size: 30px;
				line-height: 110%;
			}
		}
		.logo-nogac {
			width: 60px;
		}
		.logo-vragolasti {
			width: 160px;
		}
	}

	${media.largeTablet} {
		.info-container {
			margin: 40px auto;

			.info-author-name {
				font-size: 35px;
			}
		}
	}
	${media.smallDesktop} {
		padding: 0px 40px 20px 40px;

		.quiz-mode-titlewrap {
			margin-top: 0;
		}

		.quiz-mode-title {
			font-size: clamp(32px, 4.5vw, 44px);
			padding: 0;
			transform: none;
			/* drops the greeting into the framed cover instead of hugging
			   the top bar */
			margin-top: 53px;
		}

		.quiz-mode-rows {
			margin-top: 60px;
		}

		.quiz-start-title {
			font-size: clamp(38px, 4.6vw, 68px);
			line-height: 0.98;
		}

		.quiz-start-author {
			display: flex;
		}

		.quiz-start-head {
			padding: 0;
		}

		/* Desktop: the stacked rows become one horizontal SpecBar — the
		   container carries the full-bleed hairlines and the page gutter as
		   padding, cells separate with hairlines and share the row. */
		.quiz-start-rows {
			margin-top: 44px;
			display: flex;
			align-items: stretch;
			margin-left: calc(50% - 50vw / var(--fit-zoom, 1));
			margin-right: calc(50% - 50vw / var(--fit-zoom, 1));
			padding-left: calc(50vw / var(--fit-zoom, 1) - 50%);
			padding-right: calc(50vw / var(--fit-zoom, 1) - 50%);
			border-top: 1px solid
				color-mix(
					in srgb,
					${({ theme }) => theme.palette.quiz.textMain} 15%,
					transparent
				);
			border-bottom: 1px solid
				color-mix(
					in srgb,
					${({ theme }) => theme.palette.quiz.textMain} 15%,
					transparent
				);
			/* one glass band across the whole SpecBar (cells reset theirs) */
			${glassBand}
		}

		.quiz-start-row {
			margin: 0;
			padding: 24px 28px;
			border-top: none;
			flex: 1;
			min-width: 0;
			gap: 8px;
			align-content: start;
			background: none;
			backdrop-filter: none;
			-webkit-backdrop-filter: none;
			border-right: 1px solid
				color-mix(
					in srgb,
					${({ theme }) => theme.palette.quiz.textMain} 15%,
					transparent
				);

			&:first-of-type {
				padding-left: 0;
			}

			&:last-of-type {
				border-bottom: none;
				border-right: none;
				padding-right: 0;
			}

			&.grow {
				flex: 1.5;
			}
		}

		.quiz-mode-row {
			/* Desktop keeps the hairline list: the rows sit beside the framed
			   cover rather than on it, so they need the rules to read as one
			   list and not the halo the phone layout uses over the photo. */
			border-top: 1px solid
				color-mix(
					in srgb,
					${({ theme }) => theme.palette.quiz.textMain} 15%,
					transparent
				);
			text-shadow: none;

			&:last-of-type {
				border-bottom: 1px solid
					color-mix(
						in srgb,
						${({ theme }) => theme.palette.quiz.textMain} 15%,
						transparent
					);
			}

			.in {
				grid-template-columns: 70px 1fr minmax(0, 400px) auto;
				gap: clamp(14px, 3vw, 44px);
				padding: 40px 10px;
			}

			.main h3 {
				font-size: clamp(26px, 2.6vw, 44px);
			}

			.desc {
				font-size: 16px;
				text-align: right;
				justify-self: end;
			}

			.arr {
				width: 54px;
				height: 54px;
			}
		}

		/* /play only: the last column holds the »saznaj više« + code block
		   on the TV row and the 54px arrow on the device row — a shared
		   fixed width keeps both descriptions flush to one right edge. */
		.quiz-play-rows .quiz-mode-row .in {
			grid-template-columns: 70px 1fr minmax(0, 400px) 260px;
		}

		/* Desktop: the trigger leaves the code block and sits below the
		   right-aligned description; the code keeps the last column alone. */
		.quiz-play-more.mob {
			display: none;
		}

		.quiz-play-more.desk {
			display: flex;
			width: max-content;
			margin: 10px 0 0 auto;
		}

		.quiz-play-code-block {
			justify-content: flex-end;
		}

		.quiz-play-code {
			height: 60px;

			span {
				font-size: 52px;
			}
		}
		.info-container {
			display: flex;
			flex-direction: column;
			justify-content: center;
			max-width: 1000px;
			width: 100%;
			margin: 40px auto;

			.info {
				background-color: ${({ theme }) =>
					theme.palette.quiz.surfaceTransparent};
				border-radius: 100px;
				display: flex;
				flex-grow: 1;
				align-items: center;
				padding: 15px;

				& > :nth-child(1) {
					padding: 0px 20px;
					text-align: right;
					font-size: 25px;
					max-width: 245px;
				}

				& > :nth-child(2) {
					padding: 20px;
					flex-basis: 73%;
				}
			}
			.info-author-name {
				font-size: 35px;
			}
		}

		.logo-nogac {
			width: 80px;
		}
		.logo-vragolasti {
			width: 190px;
		}
	}
	${media.largeDesktop} {
		.info-container {
			.info-author-name {
				font-size: 40px;
			}
		}
	}
	${media.extraLargeDesktop} {
		.info-container {
			.info-author-name {
				font-size: 45px;
			}
		}
	}

	/* /mode only: the QAQA/QQAA sequence must NEVER line-break on desktop,
	   and no side column gives it room (the phrase runs ~590px at its clamp
	   font while the shared 4-column row leaves ~300-450px) — so the row
	   restacks: nowrap sequence on the first line with the whole row width
	   to itself, description in a wide measure beneath it, arrow riding
	   both lines. */
	@media (min-width: 1080px) {
		&[data-id='quiz-mode-selection-container-00'] {
			.quiz-mode-row .in {
				grid-template-columns: 70px minmax(0, 1fr) auto;
				row-gap: 10px;
			}

			.quiz-mode-row .main {
				grid-row: 1;
				grid-column: 2;

				h3 {
					white-space: nowrap;
				}
			}

			.quiz-mode-row .desc {
				grid-row: 2;
				grid-column: 2;
				/* the paragraph may run the full track, up to the arrow — the
				   column gap is all the separation it needs */
				max-width: none;
				text-align: left;
				justify-self: start;
			}

			.quiz-mode-row .arr {
				grid-row: 1 / span 2;
				grid-column: 3;
				align-self: center;
			}
		}
	}

	/* Wide desktops go back to the original one-line row — description
	   between the nowrap sequence and the arrow — but the description
	   column is flexible (all the leftover width) instead of a 400px cap:
	   right-aligned, its text edge always ends one column gap from the
	   arrow. Below this width the leftover next to the unbreakable phrase
	   drops under ~500px and the stacked layout above takes over. */
	@media (min-width: 1500px) {
		&[data-id='quiz-mode-selection-container-00'] {
			.quiz-mode-row .in {
				grid-template-columns: 70px auto minmax(0, 1fr) auto;
			}

			.quiz-mode-row .desc {
				grid-row: 1;
				grid-column: 3;
				/* a fifth narrower than the full leftover track — the measure
				   stays comfortable while the right edge keeps hugging the
				   arrow */
				max-width: 80%;
				text-align: right;
				justify-self: end;
			}

			.quiz-mode-row .arr {
				grid-row: 1;
				grid-column: 4;
			}
		}
	}

	/* /play wide desktops, same treatment as /mode: the title column takes
	   its natural width and the description the leftover track (capped a
	   fifth short of it), right-aligned against the code/arrow column —
	   instead of a 400px paragraph stranded mid-row. */
	@media (min-width: 1500px) {
		&[data-id='quiz-play-container-00'] {
			.quiz-play-rows .quiz-mode-row .in {
				grid-template-columns: 70px auto minmax(0, 1fr) 260px;
			}

			.quiz-mode-row .desc {
				max-width: 80%;
			}
		}
	}

	/* Height-constrained desktops re-flow BEFORE the FitToViewport zoom
	   touches any font: tighter verticals buy the height back at full font
	   size, and the zoom takes over only once even the compact layout
	   cannot fit the viewport. */
	@media (min-width: 1080px) and (max-height: 850px) {
		&[data-id='quiz-mode-selection-container-00'] {
			.quiz-mode-title {
				margin-top: 20px;
			}

			.quiz-mode-rows {
				margin-top: 32px;
			}

			.quiz-mode-row .in {
				padding: 26px 10px;
			}

			.quiz-mode-template {
				margin-top: 20px;
			}
		}
	}

	@media (min-width: 1080px) and (max-height: 700px) {
		&[data-id='quiz-play-container-00'] {
			.quiz-mode-title {
				margin-top: 20px;
			}

			.quiz-mode-rows {
				margin-top: 32px;
			}

			.quiz-mode-row .in {
				padding: 26px 10px;
			}
		}
	}
`;

export const BottomNavContainer = styled.div`
	z-index: 4;
	position: fixed;
	left: 50%;
	bottom: 0;
	transform: translateX(-50%);
	width: 100vw;
	height: 100px;
	padding: 0 20px;
	box-sizing: border-box;
	background-color: ${({ theme }) => theme.palette.quiz.navigationBackground};
	display: flex;
	justify-content: center;

	.bottom-nav-content {
		position: relative;
		width: 100%;
		max-width: 1860px;
		height: 100%;
		display: flex;
		align-items: center;
		gap: 26px;
	}

	.active {
		color: ${({ theme }) => theme.palette.quiz.textBrand};
	}

	.bottom-nav-feedback {
		display: flex;
		align-items: center;
		gap: 22px;
		min-width: 0;
	}

	.level-chips {
		display: flex;
		gap: 8px;
		margin-left: 14px;
	}

	/* arrows center on the PAGE axis, not on the leftover flex space */
	.bottom-nav-center {
		position: absolute;
		left: 50%;
		transform: translateX(-50%);
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 30px;
	}

	.bottom-nav-actions {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 36px;
		margin-left: auto;
	}

	.bottom-nav-report,
	.thumb {
		cursor: pointer;
	}

	.bottom-nav-report {
		text-transform: uppercase;
		letter-spacing: 0.8px;
	}

	.thumb {
		display: flex;
		transition: transform 0.25s;

		&:hover {
			transform: scale(1.1);
		}
	}

	.flex {
		display: flex;
	}

	.qmark {
		width: 20px;
		height: 20px;
		flex-shrink: 0;
		margin-left: 10px;
		border-radius: 50%;
		border: 1.5px solid
			color-mix(
				in srgb,
				${({ theme }) => theme.palette.quiz.textMain} 35%,
				transparent
			);
		font-family: ${({ theme }) => theme.typography.fontSecondary};
		font-weight: 700;
		font-size: 11px;
		justify-content: center;
		cursor: help;

		/* the Q-logo glyph follows the circle's color */
		svg path {
			fill: currentColor;
		}
	}

	.tooltip {
		position: relative;
		display: flex;
		align-items: center;
		color: ${({ theme }) => theme.palette.quiz.textMuted};
		font-size: 15px;
	}

	.tooltip .tooltiptext {
		visibility: hidden;
		width: 240px;
		background-color: ${({ theme }) => theme.palette.quiz.surfaceSoft};
		border-radius: 10px;
		padding: 8px 12px;
		position: absolute;
		z-index: 6;
		left: 50%;
		bottom: calc(100% + 10px);
		top: auto;
		transform: translateX(-50%);
		text-align: left;
	}

	.tooltip:hover .tooltiptext {
		visibility: visible;
	}
`;

export const OptionsKebab = styled.div`
	flex-basis: 30%;

	.kebab-menu {
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 30px;
		height: 30px;
		border-radius: 50%;
	}

	.kebab-icon {
		font-size: 24px;
		color: ${({ theme }) => theme.palette.quiz.textMuted};
	}

	.menu-container {
		position: relative;
		display: flex;
		justify-content: flex-end;
	}

	.dropdown-menu {
		position: absolute;
		bottom: 100%;
		right: 20px;
		background-color: ${({ theme }) => theme.palette.quiz.surfaceMain};
		color: ${({ theme }) => theme.palette.quiz.textMain};
		border-radius: 8px;
		width: 155px;
		z-index: 10;
		padding: 8px 0;
	}

	.dropdown-menu ul {
		list-style: none;
		margin: 0;
		padding: 0;
	}

	.dropdown-menu li {
		padding: 10px 15px;
		cursor: pointer;
		transition: background 0.2s;
		display: flex;
		align-items: center;
	}

	.dropdown-menu li:hover {
		background-color: ${({ theme }) => theme.palette.quiz.surfaceSoft};
	}

	${media.smallDesktop} {
		flex-basis: unset;

		.menu-container {
			position: relative;
		}

		.dropdown-menu {
			width: 200px;
		}
	}
`;

export const FeedbackBottom = styled.div`
	/* sort after the slide container's ::after spacer so the feedback row
	   stays anchored above the fixed arrow bar while the slide content
	   floats at the lower third */
	order: 1;

	/* single feedback row: thumbs left, level chips + (?) right, with clear
	   distance from the fixed arrow row below */
	.feedback-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
		padding: 18px 8px 10px;
	}

	.feedback-row .thumbs {
		display: flex;
		align-items: center;
		gap: 16px;
	}

	.feedback-row .thumb {
		display: flex;
		cursor: pointer;
	}

	.feedback-row .level {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	/* generator KChips sized down for the phone row */
	.feedback-row .level button:not(.qmark) {
		font-size: 12px;
		padding: 7px 12px;
	}

	.feedback-row .qmark {
		width: 20px;
		height: 20px;
		flex-shrink: 0;
		margin-left: 8px;
		padding: 0;
		border-radius: 50%;
		background: transparent;
		border: 1.5px solid
			color-mix(
				in srgb,
				${({ theme }) => theme.palette.quiz.textMain} 35%,
				transparent
			);
		color: ${({ theme }) => theme.palette.quiz.textMuted};
		font-family: ${({ theme }) => theme.typography.fontSecondary};
		font-weight: 700;
		font-size: 11px;
		display: inline-flex;
		align-items: center;
		justify-content: center;

		/* the Q-logo glyph follows the circle's color */
		svg path {
			fill: currentColor;
		}
	}

	.actions-container {
		display: flex;
		flex-direction: column;
		gap: 20px;
		margin: 20px 0px 20px 0px;
	}

	.bottom-nav {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		position: relative;
	}

	.actions {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 30px;
		flex-basis: 30%;
	}

	.navigation {
		background-color: ${({ theme }) => theme.palette.transparent.deep};
		padding: 10px 20px;
		flex-direction: row;
		justify-content: space-between;
		margin: 5px 0px 0px 0px;
	}

	.fixed-container {
		position: fixed;
		bottom: 0px;
		width: 100%;
		left: 50%;
		transform: translateX(-50%);
		z-index: 10;
		background-color: ${({ theme }) => theme.palette.transparent.deep};
		padding-bottom: env(safe-area-inset-bottom);
	}

	.active {
		color: ${({ theme }) => theme.palette.quiz.textBrand};
	}

	.side-item {
		position: absolute;
		right: 20px;
	}

	${media.tablet} {
		.actions-container {
			display: flex;
			flex-direction: row;
			gap: 80px;
			justify-content: center;
			margin: 20px 0px 20px 0px;
		}

		.feedback {
			width: 50%;
		}
	}
`;

export const QuestionText = styled(motion.div)<{
	$small?: boolean;
	$extraSmall?: boolean;
}>`
	font-family: ${({ theme }) => theme.typography.fontSecondary};
	color: ${({ theme }) => theme.palette.quiz.textMain};

	max-width: 100%;
	text-align: left;
	flex: 1;
	margin: 20px 0px 30px;
	font-weight: 500;

	font-size: ${({ $small, $extraSmall }) =>
		$extraSmall ? '14px' : $small ? '16px' : '18px'};
	line-height: 115%;

	${media.mobile} {
		font-size: ${({ $small, $extraSmall }) =>
			$extraSmall ? '16px' : $small ? '18px' : '20px'};
	}
	${media.tablet} {
		font-size: ${({ $small, $extraSmall }) =>
			$extraSmall ? '18px' : $small ? '22px' : '25px'};
	}
	${media.largeTablet} {
		font-size: ${({ $small, $extraSmall }) =>
			$extraSmall ? '20px' : $small ? '25px' : '30px'};
	}

	${media.smallDesktop} {
		font-size: ${({ $small, $extraSmall }) =>
			$extraSmall ? '25px' : $small ? '31px' : '40px'};
		margin: 20px 0px 40px;
		line-height: 115%;
	}
	${media.largeDesktop} {
		font-size: ${({ $small, $extraSmall }) =>
			$extraSmall ? '29px' : $small ? '35px' : '44px'};
		line-height: 130%;
	}
	${media.extraLargeDesktop} {
		font-size: ${({ $small, $extraSmall }) =>
			$extraSmall ? '32px' : $small ? '37px' : '44px'};
		line-height: 130%;
	}
	${media.ultrawide} {
		font-size: ${({ $small, $extraSmall }) =>
			$extraSmall ? '40px' : $small ? '46px' : '52px'};
		line-height: 140%;
	}

	.flex {
		display: flex;
	}

	.option-text {
		align-self: center;
	}

	ul {
		margin: 10px 0px;
	}
`;

export const QuestionNumbers = styled.div`
	/* Damps the FitToViewport slide zoom so the points circle shrinks at
	   sqrt of the slide zoom — slower than the content around it; 1 outside
	   the play stage. */
	zoom: var(--fit-unzoom, 1);
	border-radius: 50%;
	background-color: ${({ theme }) => theme.palette.quiz.pointsBackground};

	font-family: ${({ theme }) => theme.typography.fontPrimary};
	color: ${({ theme }) => theme.palette.quiz.pointsText};
	min-width: 40px;
	height: 40px;
	font-size: 25px;

	display: flex;
	justify-content: center;
	align-items: center;
	margin: 0px 20px;

	${media.tablet} {
		min-width: 45px;
		height: 45px;
		font-size: 30px;
	}

	${media.smallDesktop} {
		min-width: 60px;
		height: 60px;
		font-size: 40px;
		margin: 0px 20px 0px 55px;
	}

	${media.ultrawide} {
		min-width: 70px;
		height: 70px;
		font-size: 50px;
		margin: 0px 20px 0px 55px;
	}
`;

export const QuestionPointsExplanation = styled.div`
	font-family: ${({ theme }) => theme.typography.fontSecondary};
	font-weight: 400;
	text-align: left;
	color: ${({ theme }) => theme.palette.quiz.textMain};
	position: relative;
	z-index: 2;

	display: flex;
	align-self: center;
	font-size: 12px;
	line-height: 115%;

	${media.tablet} {
		font-size: 15px;
	}

	${media.smallDesktop} {
		display: block;
		font-size: 16px;
	}

	${media.largeDesktop} {
		font-size: 20px;
	}

	${media.ultrawide} {
		font-size: 30px;
	}
`;

export const QuestionTitle = styled.div<{ $small?: boolean }>`
	/* Damps the FitToViewport slide zoom so the BODOVI/ODGOVOR labels shrink
	   at sqrt of the slide zoom — slower than the content around them; 1
	   outside the play stage. */
	zoom: var(--fit-unzoom, 1);
	font-family: ${({ theme }) => theme.typography.fontPrimary};
	color: ${({ theme }) => theme.palette.quiz.textMuted};
	font-weight: 700;
	text-transform: uppercase;
	text-align: left;
	font-size: ${({ $small }) => ($small ? '20px' : '25px')};
	line-height: 180%;

	${media.tablet} {
		font-size: ${({ $small }) => ($small ? '25px' : '30px')};
	}

	${media.largeTablet} {
		font-size: ${({ $small }) => ($small ? '30px' : '35px')};
	}

	${media.smallDesktop} {
		font-size: ${({ $small }) => ($small ? '35px' : '40px')};
		margin: 16px 0px;
	}

	${media.ultrawide} {
		font-size: ${({ $small }) => ($small ? '40px' : '45px')};
	}
`;

export const QuestionNumber = styled.div<{ $small?: boolean }>`
	font-family: ${({ theme }) => theme.typography.fontPrimary};
	color: ${({ theme, $small }) =>
		$small ? theme.palette.quiz.textMuted : theme.palette.quiz.textMain};
	text-transform: uppercase;
	font-weight: 400;
	text-align: left;
	letter-spacing: 1px;
	margin-right: 5px;
	white-space: nowrap;

	font-size: ${({ $small }) => ($small ? '15px' : '20px')};

	${media.smallDesktop} {
		font-size: ${({ $small }) => ($small ? '24px' : '32px')};
	}
`;

export const QuestionFeedbackTitle = styled.div<{
	$grayedOut?: boolean;
	$small?: boolean;
}>`
	font-family: ${({ theme }) => theme.typography.fontPrimary};
	color: ${({ theme, $grayedOut }) =>
		$grayedOut ? theme.palette.quiz.textMuted : theme.palette.quiz.textSoft};

	margin: 6px 0px;

	font-size: 14px;
	line-height: 24px;

	${media.smallDesktop} {
		font-size: ${({ $small }) => ($small ? '12px' : '16px')};
		line-height: 115%;
	}
`;

export const QuestionFeedbackSubtitle = styled.div`
	font-family: ${({ theme }) => theme.typography.fontPrimary};
	color: ${({ theme }) => theme.palette.quiz.textMuted};
	font-size: 10px;
	line-height: 12px;
	margin: 5px 0px 20px;

	${media.smallDesktop} {
		text-align: center;
		font-size: 12px;
		line-height: 15px;
	}
`;

/* Round slide-navigation buttons per the redesign system: bordered ghost for
   previous, filled teal for next (className='pri'). */
export const PlayNavButton = styled.button<{ $small?: boolean }>`
	width: ${({ $small }) => ($small ? '48px' : '62px')};
	height: ${({ $small }) => ($small ? '48px' : '62px')};
	border-radius: 50%;
	border: 2px solid
		${({ theme }) =>
			`color-mix(in srgb, ${theme.palette.quiz.textMain} 35%, transparent)`};
	background: transparent;
	color: ${({ theme }) => theme.palette.quiz.textMain};
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	padding: 0;
	transition:
		transform 0.18s,
		border-color 0.18s,
		box-shadow 0.18s;
	-webkit-tap-highlight-color: transparent;

	&:hover {
		transform: scale(1.08);
		border-color: ${({ theme }) => theme.palette.quiz.textMain};
	}

	&:active {
		transform: scale(0.88);
	}

	&.pri {
		background: ${({ theme }) => theme.palette.brand.main};
		border-color: ${({ theme }) => theme.palette.brand.main};
		color: ${({ theme }) => theme.palette.brand.night};
	}

	svg path {
		fill: currentColor;
	}
`;

export const QuestionFeedbackOption = styled.div`
	font-family: ${({ theme }) => theme.typography.fontPrimary};
	color: ${({ theme }) => theme.palette.quiz.textSoft};
	font-weight: 600;
	font-size: 18px;
	text-transform: uppercase;
	letter-spacing: 0.5px;

	.active {
		color: ${({ theme }) => theme.palette.quiz.textBrand};
	}

	${media.smallDesktop} {
		margin: 0px 10px 0px 0px;
		text-align: left;
		font-size: 25px;
		cursor: pointer;

		&:hover {
			transform: ${growTransition};
			color: ${({ theme }) => theme.palette.quiz.textBrand};
		}
	}
`;

export const QuestionOption = styled.div`
	font-family: ${({ theme }) => theme.typography.fontPrimary};
	font-weight: 500;

	color: ${({ theme }) => theme.palette.quiz.textMuted};
	margin-right: 10px;
	margin-left: 20px;

	font-size: 24px;
	line-height: 115%;

	${media.tablet} {
		font-size: 30px;
	}
	${media.largeTablet} {
		font-size: 34px;
	}
	${media.smallDesktop} {
		font-size: 38px;
		margin-left: 0px;
		margin-right: 30px;
	}

	${media.ultrawide} {
		font-size: 55px;
		line-height: 65px;
	}
`;

export const QuestionContainer = styled(motion.div)<{
	color: string;
	$answer?: boolean;
}>`
	background-color: ${({ color, theme }) => handleColorType(color, theme)};

	position: relative;
	z-index: 2;

	/* breathing room for question text, options, points and images on the
	   mobile scroll layout — the desktop block below overrides it */
	padding: 0px 16px;

	${({ $answer }) =>
		$answer &&
		`
		border-radius: 16px;
		padding: 12px 16px;
		margin: 20px 0px;
	`}

	.flex {
		display: flex;
	}
	.question-number {
		justify-content: flex-start;
		align-items: flex-end;
		text-transform: uppercase;
	}
	.marginB {
		margin-bottom: 20px;
	}
	.points {
		align-items: center;
		position: relative;
		z-index: 2;
		margin-top: 30px;
	}

	.connection-option {
		display: flex;
		justify-content: flex-start;
		align-items: flex-start;
		flex-basis: 50%;
	}
	.answer-text {
		color: ${({ theme }) => theme.palette.quiz.textStrong};
		position: relative;
		z-index: 1;
		margin: 10px 0px;
	}

	.answer-explanation {
		display: flex;
		justify-content: flex-start;
		align-items: center;
		margin: 10px 0px;

		.text {
			margin: 0px 0px 0px 10px;
		}
	}

	${media.smallDesktop} {
		border-radius: 16px;
		padding: 2px 30px;
		display: flex;
		justify-content: flex-start;
		align-items: flex-start;
		margin: 0px 0px 40px 0px;

		/* Answer text aligns to the counter-zoomed ODGOVOR label, so its
		   offset must stay visually constant like the label itself. */
		.answer {
			margin: calc(25px * var(--fit-unzoom, 1)) 0px;
			flex: 1;
			z-index: 3;
		}
		/* The points column hosts the counter-zoomed BODOVI label + circle,
		   so its slot must follow the same damped rate — otherwise the label
		   overflows it when the slide zooms out. */
		.points {
			width: calc(300px * var(--fit-unzoom, 1));
			margin-left: 30px;
			margin-top: 0px;
			z-index: 3;
		}
		.points-explanation {
			margin: 0px 0px 20px 0px;
			z-index: 3;
		}
		.answer-text {
			margin: 0px 0px 10px 0px;
			z-index: 3;
		}
		.association-text {
			margin: 20px 0px 0px;
		}
	}

	${media.largeDesktop} {
		.points {
			width: calc(350px * var(--fit-unzoom, 1));
		}
	}

	${media.ultrawide} {
		.answer {
			margin: calc(15px * var(--fit-unzoom, 1)) 0px;
		}
		.points {
			width: calc(400px * var(--fit-unzoom, 1));
		}
	}
`;

export const PremadeFeedbackContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 30px;
	margin: 20px 0px 40px 0px;
	text-align: center;
	justify-content: center;
	align-items: center;

	.action-container {
		display: flex;
		gap: 15px;
		justify-content: center;
	}
	.feedback-hand {
		margin: 10px 0px;
	}

	/* quality 1–5 reuses the quick-quiz number picker (BqNums), scaled
	   down and recolored for the play theme */
	.feedback-nums {
		justify-content: center;
		gap: clamp(22px, 3vw, 44px);
		padding: 6px 0 0;

		/* half the quick-quiz size */
		button {
			font-size: clamp(27px, 3.25vw, 52px);
			color: ${({ theme }) => theme.palette.quiz.textMuted};
		}

		button:hover,
		button.on {
			color: ${({ theme }) => theme.palette.quiz.textMain};
		}

		button::after {
			height: 8px;
			bottom: 3px;
		}
	}

	${media.largeTablet} {
		gap: 40px;

		.action-container {
			gap: 25px;
		}
		.feedback-hand {
			margin: 20px 0px;
		}
	}
`;

export const Level = styled(motion.div)`
	font-family: ${({ theme }) => theme.typography.fontPrimary};
	color: ${({ theme }) => theme.palette.quiz.textSoft};
	font-weight: 600;
	font-size: 18px;
	margin: 10px 0px;

	&:hover {
		transform: ${growTransition};
		color: ${({ theme }) => theme.palette.quiz.textBrand};
	}

	${media.smallDesktop} {
		margin: 10px 0px;
		text-align: left;
		font-size: 35px;
		cursor: pointer;
	}
`;

export const NavButtonWrapper = styled.div`
	position: fixed;
	bottom: 0px;
	width: 100%;
	left: 50%;
	transform: translateX(-50%);
	z-index: 10;
	background-color: ${({ theme }) => theme.palette.transparent.deep};
	padding-bottom: env(safe-area-inset-bottom);
	height: 65px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 4px 0px 0px;
	font-family: ${({ theme }) => theme.typography.fontSecondary};
	color: ${({ theme }) => theme.palette.quiz.textReverse};
	gap: 10px;

	/* label typography mirrors the redesign button primitives (btnBase) */
	p {
		font-family: var(--fd);
		font-size: 17px;
		letter-spacing: 0.4px;
		text-transform: lowercase;
	}

	${media.smallDesktop} {
		background-color: ${({ theme }) => theme.palette.transparent.deep};
		color: ${({ theme }) => theme.palette.quiz.brandMain};
		position: fixed;
		left: 50%;
		bottom: 0;
		transform: translateX(-50%);
		margin: 0;
		width: 100vw;
		height: 100px;
		padding: 0 20px;
		box-sizing: border-box;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 0;

		&:hover {
			background-color: ${({ theme }) => theme.palette.quiz.brandMain};
			transition: 0.3s;
			cursor: pointer;
		}
	}
`;

export const AssociationContainer = styled.div`
	.association-indicator {
		font-size: 44px;
		font-weight: 600;
		font-family: ${({ theme }) => theme.typography.fontPrimary};
		color: ${({ theme }) => theme.palette.quiz.indicator};
		text-transform: uppercase;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		margin-left: 10px;
		margin-bottom: 10px;
	}
	.association-option .answer-text {
		color: ${({ theme }) => theme.palette.quiz.textBrand};
		font-family: ${({ theme }) => theme.typography.fontPrimary};
		text-transform: uppercase;
		letter-spacing: 1px;
	}
	.association-option-hidden .tap-hint {
		font-size: 8.5px;
		letter-spacing: 1.6px;
		text-transform: uppercase;
		color: ${({ theme }) => theme.palette.quiz.textMuted};
		padding: 0 3px;
	}
	.association-option-hidden .hidden-letter {
		font-size: 34px;
		line-height: 1;
		font-weight: 600;
		font-family: ${({ theme }) => theme.typography.fontPrimary};
		color: ${({ theme }) => theme.palette.quiz.textMain};
		text-transform: uppercase;
	}
	.association-option-hidden {
		height: 200px;
		border-radius: 30px;
		background: color-mix(
			in srgb,
			${({ theme }) => theme.palette.quiz.textMain} 8%,
			transparent
		);
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: space-between;
		padding: 24px 0 18px;
		cursor: pointer;
		border: 1px solid
			color-mix(
				in srgb,
				${({ theme }) => theme.palette.quiz.textMain} 14%,
				transparent
			);
	}
	.column-letter {
		font-family: ${({ theme }) => theme.typography.fontPrimary};
		color: ${({ theme }) => theme.palette.quiz.textMuted};
		text-transform: uppercase;
		letter-spacing: 1.5px;
		font-size: 16px;
	}
	.association-option {
		font-size: 16px;
		line-height: 16px;
		margin: 5px 0;
		padding: 10px 10px;
		height: 45px;
		border-radius: 35px;
		background: color-mix(in srgb, ${designBg} 68%, transparent);
		color: ${({ theme }) => theme.palette.quiz.textMain};
		text-align: center;
		display: flex;
		justify-content: center;
		align-items: center;
		position: relative;
		z-index: 2;
		border: 1px solid
			color-mix(
				in srgb,
				${({ theme }) => theme.palette.quiz.textMain} 14%,
				transparent
			);
	}
	.final {
		min-height: 45px;
	}
	.indicator {
		${disableUserSelect}

		position: absolute;
		bottom: 13px;
		left: 19px;
		font-size: 30px;
		font-weight: 600;
		color: ${({ theme }) => theme.palette.quiz.surfaceMuted};
		text-transform: uppercase;
		z-index: 0;
		font-family: ${({ theme }) => theme.typography.fontPrimary};
	}

	.preview {
		background: color-mix(
			in srgb,
			${({ theme }) => theme.palette.quiz.textMain} 8%,
			transparent
		);
	}

	.hidden-row {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 8px;
		margin: 16px 0 10px;
	}
	.column {
		width: 100%;
	}

	${media.tablet} {
		.association-option {
			font-size: 20px;
			line-height: 20px;
		}
		.final {
			min-height: 42px;
		}
	}

	${media.largeTablet} {
		.association-option {
			font-size: 22px;
			line-height: 22px;
		}
		.final {
			min-height: 45px;
		}
	}
	.info {
		display: flex;
		align-items: center;
		margin: 10px 20px;
	}

	${media.smallDesktop} {
		position: relative;
		z-index: 2;
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: 10px;
		width: 100%;
		padding: 0 48px;
		box-sizing: border-box;
		line-height: 100%;
		margin-top: 20px;

		/* Proposal pills: quiet hairline cells; clues sit on the glass
		   surface, the bottom row carries the muted column letter on the
		   question slide and the brand-colored solution on the answer. */
		.cell {
			border-radius: 50px;
			min-height: 56px;
			padding: 8px 16px;
			background: color-mix(
				in srgb,
				${({ theme }) => theme.palette.quiz.textMain} 8%,
				transparent
			);
			border: 1px solid
				color-mix(
					in srgb,
					${({ theme }) => theme.palette.quiz.textMain} 14%,
					transparent
				);
			display: flex;
			align-items: center;
			justify-content: center;
			text-align: center;
			font-family: ${({ theme }) => theme.typography.fontPrimary};
			font-size: 30px;
			letter-spacing: 0.6px;
			color: ${({ theme }) => theme.palette.quiz.textMuted};

			p {
				margin: 0;
				display: inline;
			}
		}

		.cell.clue {
			background: color-mix(in srgb, ${designBg} 68%, transparent);
			color: ${({ theme }) => theme.palette.quiz.textMain};
			font-family: ${({ theme }) => theme.typography.fontSecondary};
			font-weight: 500;
			font-size: 31px;
			letter-spacing: normal;
			line-height: 1.2;
		}

		.cell.sol {
			background: color-mix(in srgb, ${designBg} 68%, transparent);
			color: ${({ theme }) => theme.palette.quiz.textBrand};
			letter-spacing: 1px;
			text-transform: uppercase;
		}

		.cell.final-row {
			grid-column: 1 / -1;
		}
	}

	${media.ultrawide} {
		.cell {
			min-height: 60px;
			font-size: 35px;
		}

		.cell.clue {
			font-size: 36px;
		}
	}
`;

/* Redesign navigator (mobile only — on desktop the track moves into the
   top play bar, see NavPlayProgress): full-width pill track with the
   counter left of it; break icons take the counter's slot. */
export const QuizNavigator = styled.div`
	display: flex;
	width: 100%;
	align-items: center;
	gap: 8px;
	margin-bottom: 14px;
	/* the page shell pads 10px on mobile; +4px lines the row up with the
	   FramedCover inset (14px) like the top nav */
	padding: 0 4px;

	/* fixed-size slot so the progress bar keeps the same width and vertical
	   position whatever renders next to it (numbers, break icon, or nothing) */
	.quiz-navigator-indicator {
		display: flex;
		align-items: center;
		justify-content: center;
		flex: 0 0 58px;
		width: 58px;
		height: 32px;
	}

	.quiz-navigator-numbers {
		display: flex;
		align-items: baseline;
		justify-content: flex-start;

		> *:last-child {
			margin-right: 0;
		}
	}

	.quiz-navigator-icon {
		display: flex;
		align-items: center;
		justify-content: center;
		color: ${({ theme }) => theme.palette.quiz.brandMain};
	}

	/* estimation steps: »procjena« wordmark instead of an icon — sized to
	   fit the fixed 58px slot */
	.quiz-navigator-text {
		font-family: ${({ theme }) => theme.typography.fontPrimary};
		text-transform: uppercase;
		letter-spacing: 0.5px;
		font-size: 12px;
		white-space: nowrap;
		/* same ink as the question counter */
		color: ${({ theme }) => theme.palette.quiz.textMain};
	}

	${media.smallDesktop} {
		display: none;
	}
`;

/* Desktop only: the navigator rides inside the top play bar, stretched
   between the logo lockup and the controls — track first, counter/break
   icon beside it. Rendered by Quiz.tsx and slotted into TopNav. */
export const NavPlayProgress = styled.div`
	display: none;

	${media.smallDesktop} {
		display: flex;
		align-items: center;
		gap: 14px;
		flex: 1;
		min-width: 0;
		margin: 0 8px;
	}

	/* fixed-size slot so the track keeps the same width whatever renders
	   next to it (numbers, break icon, or nothing) */
	.quiz-navigator-indicator {
		display: flex;
		align-items: center;
		justify-content: center;
		flex: 0 0 auto;
		min-width: 72px;
		height: 42px;
	}

	.quiz-navigator-numbers {
		display: flex;
		align-items: baseline;
	}

	.quiz-navigator-icon {
		display: flex;
		align-items: center;
		justify-content: center;
		color: ${({ theme }) => theme.palette.quiz.brandMain};
	}

	/* estimation steps: »procjena« wordmark instead of an icon */
	.quiz-navigator-text {
		font-family: ${({ theme }) => theme.typography.fontPrimary};
		text-transform: uppercase;
		letter-spacing: 1px;
		font-size: 16px;
		white-space: nowrap;
		/* same ink as the question counter */
		color: ${({ theme }) => theme.palette.quiz.textMain};
	}

	/* nav-scale counter — smaller than the in-page navigator's slide type */
	${QuestionNumber} {
		font-size: 17px;
	}

	${QuestionNumber}:first-child {
		font-size: 22px;
	}
`;

export const ProgressBar = styled.div<{ $progress?: number }>`
	width: 100%;
	flex: 1;
	min-width: 0;
	background-color: color-mix(
		in srgb,
		${({ theme }) => theme.palette.quiz.textMain} 13%,
		transparent
	);
	border-radius: 16px;
	overflow: hidden;
	position: relative;
	height: 20px;

	&::after {
		content: '';
		position: absolute;
		inset: 0 auto 0 0;
		width: ${({ $progress = 0 }) =>
			`${Math.min(Math.max($progress, 0), 100)}%`};
		background-color: ${({ theme }) => theme.palette.brand.main};
		border-radius: 16px;
		transition: width 0.3s ease;
	}
`;

export const PremadeQuizPlayBackground = styled.div<{ $kidsFonts?: boolean }>`
	position: relative;
	width: 100%;
	min-height: 100svh;
	overflow: hidden;

	/* for_kids: GlobalStyle sets the font vars at :root from the app theme,
	   so the play scope re-derives them from the (kids-swapped) play theme
	   and re-anchors inheritance — every var() and inherited face follows */
	${({ $kidsFonts, theme }) =>
		$kidsFonts
			? css`
					--fd: ${theme.typography.fontPrimary};
					--fb: ${theme.typography.fontSecondary};
					--fh: ${theme.typography.fontHandwritten};
					font-family: var(--fb);
				`
			: ''}

	.quiz-play-background-content {
		position: relative;
		z-index: 2;
		min-height: 100svh;
		display: flex;
		flex-direction: column;
	}
`;

/* ===================================================================== */
/* »Uokvireni zastor« play redesign (2026-08): the cover photo is an     */
/* inset rounded backdrop that dissolves into the page, answers reveal   */
/* through a full-bleed glass band, points are a handwritten note.      */
/* ===================================================================== */

/* Framed cover backdrop. Mobile: fixed-size band below the bar + counter
   row (content growth never stretches the photo). Desktop: inset frame that
   dissolves toward the page bottom. Both themes share the geometry; only the
   wash differs (»bijeljeni zastor« in light — see coverWash). */

/* Constant wash over the cover photo: espresso 56% in dark, where near-white
   text rides the darkened photo. Cream needs more — ink over 56% cream on a
   dark photo region drops to ~5:1; at 74% the worst case stays ~8.6:1. */
const coverWash = ({ theme }: { theme: any }) =>
	`color-mix(in srgb, ${theme.design?.bg ?? '#191b1d'} ${
		theme.mode === 'light' ? '74%' : '56%'
	}, transparent)`;

export const FramedCover = styled.div<{ $entry?: boolean; $blur?: boolean }>`
	position: absolute;
	z-index: 0;
	overflow: hidden;
	pointer-events: none;
	/* Full-bleed on phones: the photo runs edge to edge with no side inset;
	   the top corners keep their rounding, the bottom dissolves anyway. */
	border-radius: 24px 24px 0 0;
	/* Clears the fixed top bar AND the slide progress bar that sits under it,
	   so the counter reads against the page, not the photo. */
	inset: 128px 0 auto 0;
	height: 430px;

	/* Entry screens (TV code, mode, start) carry no progress bar, and their
	   first line is a handwritten note that has to land ON the cover — with a
	   tall row block it would otherwise ride up past a 128px edge. The frame
	   starts just under the stage's own top padding there and grows upward,
	   so its bottom stays where every other screen has it. */
	${({ $entry }) =>
		$entry
			? css`
					inset-block-start: 88px;
					height: 470px;
				`
			: ''}

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
		/* the photo eases in once loaded instead of popping over the wash */
		opacity: 0;
		transition: opacity 0.8s ease-out;

		/* The TV code and mode screens read their rows straight off the cover
		   on phones, and a busy photo (a poster collage, a crowd) fights the
		   letterforms edge for edge. Blurring it keeps the color and the mood
		   — all a 375px screen shows of it anyway — and takes the competing
		   detail away. The scale-up hides the soft rim blur leaves where it
		   samples past the frame. The start screen and the slides keep the
		   photo sharp: they carry far less type over it, and the cover is
		   part of what the players came for. */
		${({ $blur }) =>
			$blur
				? css`
						filter: blur(5px) saturate(0.85);
						transform: scale(1.06);
					`
				: ''}

		&.loaded {
			opacity: 1;
		}
	}

	/* top scrim blends the photo toward the dark chrome above — the light
	   paper needs no darkening, its wash already carries the blend */
	&::before {
		content: '';
		position: absolute;
		inset: 0 0 auto 0;
		height: 110px;
		z-index: 1;
		background: ${({ theme }) =>
			theme.mode === 'light'
				? 'none'
				: 'linear-gradient(rgba(24, 14, 2, 0.5), transparent)'};
	}

	&::after {
		content: '';
		position: absolute;
		inset: 0;
		z-index: 1;
		background: linear-gradient(
				180deg,
				transparent 30%,
				color-mix(in srgb, ${designBg} 55%, transparent) 62%,
				${designBg} 97%
			),
			${coverWash};
	}

	${media.smallDesktop} {
		border-radius: 20px;
		/* the navigator lives in the top bar on desktop, so the cover only
		   clears the bar itself */
		inset: 100px 24px 0 24px;
		height: auto;

		/* desktop shows the photo at size and keeps the rows off it — it
		   stays sharp */
		img {
			filter: none;
			transform: none;
		}

		&::after {
			background: linear-gradient(
					180deg,
					transparent 18%,
					color-mix(in srgb, ${designBg} 55%, transparent) 46%,
					color-mix(in srgb, ${designBg} 90%, transparent) 74%,
					${designBg} 93%
				),
				${coverWash};
		}
	}
`;

/* Full-bleed glass band carrying the revealed answer. */
export const RevealBand = styled(motion.div)`
	position: relative;
	z-index: 3;
	/* Full-bleed inside the fit-zoomed slide: vw units are not multiplied by
	   the CSS zoom, so the vw term divides by the slide zoom to land on the
	   real viewport edges (--fit-zoom is 1 when the slide is unscaled). */
	margin: 18px calc(50% - 50vw / var(--fit-zoom, 1)) 0;
	padding: 14px calc(50vw / var(--fit-zoom, 1) - 50%);
	text-align: left;
	border-top: 1px solid
		color-mix(
			in srgb,
			${({ theme }) => theme.palette.quiz.textMain} 15%,
			transparent
		);
	border-bottom: 1px solid
		color-mix(
			in srgb,
			${({ theme }) => theme.palette.quiz.textMain} 15%,
			transparent
		);
	background: ${({ theme }) =>
		theme.mode === 'light'
			? `color-mix(in srgb, ${
					theme.design?.panel2 ?? '#fff9ee'
				} 78%, transparent)`
			: `color-mix(in srgb, ${
					theme.design?.bg ?? '#191b1d'
				} 55%, transparent)`};
	backdrop-filter: blur(8px);
	-webkit-backdrop-filter: blur(8px);
	display: flex;
	flex-direction: column;
	gap: 4px;

	@media (max-width: 1079px) {
		backdrop-filter: none;
		-webkit-backdrop-filter: none;

		/* The band still bleeds to the viewport edges, but its text lines up
		   with the question above it — QuestionContainer's 16px mobile inset
		   on top of the page gutter the padding already restores. */
		padding-left: calc(50vw / var(--fit-zoom, 1) - 50% + 16px);
		padding-right: calc(50vw / var(--fit-zoom, 1) - 50% + 16px);
	}

	.reveal-label {
		font-family: ${({ theme }) => theme.typography.fontSecondary};
		font-weight: 700;
		font-size: 12.5px;
		letter-spacing: 2.4px;
		text-transform: uppercase;
		color: ${({ theme }) => theme.palette.quiz.textMuted};
	}

	.reveal-answer {
		font-family: ${({ theme }) => theme.typography.fontPrimary};
		letter-spacing: 0.4px;
		font-size: 25px;
		line-height: 1.15;
		color: ${({ theme }) => theme.palette.quiz.textBrand};

		p {
			margin: 0;
			display: inline;
		}

		sub,
		sup {
			font-size: 0.62em;
			line-height: 0;
		}

		/* QAQA connection answers: one letter-number pair per line, its
		   actual texts trailing as a softer parenthetical note */
		.pair {
			display: block;
			margin-bottom: 6px;
		}

		.pair-what {
			/* same face as the answer explanation note (QuestionText) */
			font-family: ${({ theme }) => theme.typography.fontSecondary};
			font-size: 15px;
			letter-spacing: 0.2px;
			text-transform: none;
			color: ${({ theme }) => theme.palette.quiz.textSoft};
		}

		/* content-pattern answers (utils/contentPattern.ts) */
		/* podpitanja: the small muted letter doubles as the separator */
		.al {
			font-size: 0.55em;
			color: ${({ theme }) => theme.palette.quiz.textMuted};
			text-transform: lowercase;
			margin: 0 9px 0 22px;

			${media.smallDesktop} {
				font-size: 0.7em;
				margin-right: 12px;
			}
		}

		/* single letters/digits sit in the glass circle (krug 7 GO:
		   varijanta A — stakleni krug); word labels stay plain */
		.al.alc {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: 2.1em;
			height: 2.1em;
			border-radius: 50%;
			border: 1.5px solid
				color-mix(
					in srgb,
					${({ theme }) => theme.palette.quiz.textMain} 30%,
					transparent
				);
			vertical-align: middle;
			margin-top: -0.25em;
		}

		.al:first-child {
			margin-left: 0;
		}

		/* each letter + answer is one unit: stacked one per line on
		   mobile, an inline run on desktop that wraps only between pairs
		   (spacing sits on the RIGHT so a pair opening a wrapped line
		   stays flush left) */
		.ap {
			display: block;
		}

		.ap .al {
			margin-left: 0;
		}

		${media.smallDesktop} {
			.ap {
				display: inline-block;
				margin-right: 22px;
			}

			.ap:last-child {
				margin-right: 0;
			}
		}

		/* poredajte: quiet arrows between the ordered members; the joining
		   spaces around the arrow carry most of the gap so the run can wrap */
		.arr {
			color: ${({ theme }) => theme.palette.quiz.textMuted};
			font-size: 0.6em;
			padding: 0 2px;
		}

		/* godina po događajima: the year with spread digits */
		.yr {
			letter-spacing: 0.2em;
		}
	}

	.reveal-why {
		font-size: 15px;
		line-height: 1.5;
		color: ${({ theme }) => theme.palette.quiz.textSoft};
		margin-top: 4px;
	}

	/* the Explanation component nests a QuestionText — inside the band it
	   inherits the note's size instead of slide-question sizing */
	.reveal-why div {
		font-size: inherit;
		line-height: inherit;
		color: inherit;
		margin: 0;
	}

	/* the »klikni za više« modal trigger: bulb and text on one line */
	.reveal-why .answer-explanation {
		display: flex;
		align-items: center;
		cursor: pointer;
	}

	${media.smallDesktop} {
		flex-direction: row;
		align-items: center;
		gap: 26px;
		margin-top: 44px;
		padding-top: 20px;
		padding-bottom: 20px;

		.reveal-label {
			width: 104px;
			flex-shrink: 0;
		}

		.reveal-answer {
			font-size: clamp(37px, 3.45vw, 55px);
		}

		.reveal-why {
			margin: 0 0 0 auto;
			min-width: 300px;
			max-width: 460px;
			font-size: 23px;
			color: ${({ theme }) => theme.palette.quiz.textMain};
			border-left: 1px solid
				color-mix(
					in srgb,
					${({ theme }) => theme.palette.quiz.textMain} 15%,
					transparent
				);
			padding-left: 22px;
		}
	}
`;

/* Handwritten Caveat points note (»1 bod«, »2 boda«, »7 bodova«). */
export const PointsNote = styled.div`
	position: relative;
	z-index: 2;
	display: flex;
	align-items: baseline;
	gap: 12px;
	margin-top: 16px;
	text-align: left;
	/* rendered as a sibling of QuestionContainer on some layouts, so it
	   mirrors the container's mobile padding; zeroed when nested below */
	padding: 0 16px;

	.val {
		font-family: ${({ theme }) => theme.typography.fontHandwritten};
		font-weight: 600;
		font-size: 26px;
		line-height: 1;
		color: var(--coral);
		display: inline-block;
		transform: rotate(-1.4deg);
		white-space: nowrap;
	}

	.expl {
		font-size: 15px;
		font-weight: 300;
		color: ${({ theme }) => theme.palette.quiz.textSoft};
	}

	${media.smallDesktop} {
		margin-top: 18px;
		/* rendered as a sibling of QuestionContainer, so it mirrors the
		   container's horizontal padding to keep the note on the question
		   text's left edge */
		padding: 0 30px;

		.val {
			font-size: 37px;
		}

		.expl {
			font-size: 19px;
		}
	}

	/* nested inside the padded QuestionContainer (mobile layouts on wide
	   screens) the container's own padding already aligns it */
	${QuestionContainer} && {
		padding: 0;
	}
`;

/* Clean borderless option list (abcd, connection): Fjalla letter + text.
   The rows follow QuestionText's font-size ladder so the options read at
   the same size as the question on every breakpoint. */
export const OptList = styled.div`
	position: relative;
	z-index: 2;
	margin-top: 16px;
	text-align: left;
	/* rendered as a sibling of QuestionContainer on some layouts, so it
	   mirrors the container's mobile padding; zeroed when nested below */
	padding: 0 16px;

	/* same ladder as QuestionText so question and options read as one size */
	.opt {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 5px 0;
		font-size: 18px;
		line-height: 115%;
	}

	.opt .L {
		font-family: ${({ theme }) => theme.typography.fontPrimary};
		color: ${({ theme }) => theme.palette.quiz.textMuted};
		min-width: 1em;
		flex-shrink: 0;
	}

	.opt .t {
		font-weight: 500;
		color: ${({ theme }) => theme.palette.quiz.textMain};
		/* editor HTML can carry non-breaking runs — wrap them inside the
		   column instead of pushing the list past the container */
		min-width: 0;
		overflow-wrap: anywhere;

		p {
			margin: 0;
			display: inline;
		}
	}

	${media.mobile} {
		.opt {
			font-size: 20px;
		}
	}
	${media.tablet} {
		.opt {
			font-size: 25px;
		}
	}
	${media.largeTablet} {
		.opt {
			font-size: 30px;
		}
	}

	${media.smallDesktop} {
		margin-top: 22px;
		/* rendered as a sibling of QuestionContainer, so it mirrors the
		   container's horizontal padding to keep the option letters on the
		   question text's left edge */
		padding: 0 30px;

		.opt {
			gap: 12px;
			padding: 8px 0;
			font-size: 40px;
		}
	}
	${media.largeDesktop} {
		.opt {
			font-size: 44px;
		}
	}
	${media.ultrawide} {
		.opt {
			font-size: 52px;
		}
	}

	/* nested inside the padded QuestionContainer (mobile layouts on wide
	   screens) the container's own padding already aligns it */
	${QuestionContainer} && {
		padding: 0;
	}
`;

/* Two connection columns on one grid; stacks on mobile. minmax(0, 1fr)
   keeps the tracks at the container width even when an option carries
   unbreakable content (bare 1fr tracks would grow past it). */
export const OptCols = styled.div`
	position: relative;
	z-index: 2;
	display: grid;
	grid-template-columns: minmax(0, 1fr);
	/* rendered as a sibling of QuestionContainer on some layouts, so it
	   mirrors the container's mobile padding; zeroed when nested below */
	padding: 0 16px;

	${media.smallDesktop} {
		/* Each column starts at 40% of the row (half-gap subtracted) and
		   widens only when its texts actually need it: a max-content growth
		   limit lets grid's maximize-tracks step feed free space to a track
		   just until its content fits, never past the container — whereas a
		   fixed-length max (50%) would soak up free space and always land
		   both columns on it. Rows also sit a touch tighter than the abcd
		   list: this grid carries twice the options. */
		grid-template-columns:
			minmax(calc(40% - 28px), max-content)
			minmax(calc(40% - 28px), max-content);
		column-gap: 56px;
		/* sibling of QuestionContainer: mirror its horizontal padding so the
		   option letters sit on the question text's left edge */
		padding: 0 30px;

		.opt {
			padding: 5px 0;
		}
	}

	/* answer slide: the number column is reordered to its matching letter,
	   and the lists dissolve into ONE shared grid (display:contents) so each
	   letter row and its number row share a track and stay in line */
	&.paired {
		margin-top: 16px;

		${OptList} {
			display: contents;
		}
	}

	${media.smallDesktop} {
		&.paired {
			margin-top: 22px;
			grid-auto-flow: column;
			grid-template-rows: repeat(4, auto);
		}
	}

	/* the wrapper carries the alignment padding, so the lists inside stay
	   flush with it */
	&& ${OptList} {
		padding: 0;
	}

	/* nested inside the padded QuestionContainer (mobile layouts on wide
	   screens) the container's own padding already aligns it */
	${QuestionContainer} && {
		padding: 0;
	}
`;

/* ================================================================== //
   Content-pattern slide bits (frontend-only detection — see
   utils/contentPattern.ts). Gold text uses design.goldInk so it stays
   readable on the light paper theme, per the U6 decision.
// ================================================================== */

const goldInk = ({ theme }: { theme: any }) =>
	theme.design?.goldInk ?? '#DBB154';

/* Pattern content follows QuestionText's font-size ladder (same rule
   OptList already applies): the parts of the text — verse lines,
   events, pills, ingredients — read at the SAME size as the question
   on every breakpoint. */
const questionScale = css`
	font-size: 18px;
	line-height: 115%;

	${media.mobile} {
		font-size: 20px;
	}
	${media.tablet} {
		font-size: 25px;
	}
	${media.largeTablet} {
		font-size: 30px;
	}
	${media.smallDesktop} {
		font-size: 40px;
	}
	${media.largeDesktop} {
		font-size: 44px;
		line-height: 130%;
	}
	${media.ultrawide} {
		font-size: 52px;
	}
`;

/* the question ladder at ~115% — gold Caveat lists, which read
   optically smaller than Ubuntu at equal px */
const handScale = css`
	font-size: 21px;

	${media.mobile} {
		font-size: 23px;
	}
	${media.tablet} {
		font-size: 29px;
	}
	${media.largeTablet} {
		font-size: 34px;
	}
	${media.smallDesktop} {
		font-size: 46px;
	}
	${media.largeDesktop} {
		font-size: 50px;
	}
	${media.ultrawide} {
		font-size: 60px;
	}
`;

/* the question ladder at ~80% — quoted verse lines (founder, krug 7:
   lyrics read a step under the question; the one-line citat variant
   stays on the full ladder because there the quote carries the slide) */
const verseScale = css`
	font-size: 14px;

	${media.mobile} {
		font-size: 16px;
	}
	${media.tablet} {
		font-size: 20px;
	}
	${media.largeTablet} {
		font-size: 24px;
	}
	${media.smallDesktop} {
		font-size: 32px;
	}
	${media.largeDesktop} {
		font-size: 35px;
	}
	${media.ultrawide} {
		font-size: 42px;
	}
`;

/* the question ladder at ~70% — recipe ingredient/prep content */
const recipeScale = css`
	font-size: 13px;
	line-height: 120%;

	${media.mobile} {
		font-size: 14px;
	}
	${media.tablet} {
		font-size: 18px;
	}
	${media.largeTablet} {
		font-size: 21px;
	}
	${media.smallDesktop} {
		font-size: 28px;
	}
	${media.largeDesktop} {
		font-size: 31px;
	}
	${media.ultrawide} {
		font-size: 36px;
	}
`;

/* Quoted text block: verse (lyrics, multi-line) or a one-line famous
   quote — a large gold quotation mark hangs in the margin. The quote
   variant carries the slide (question drops to $small below it). */
export const VerseBlock = styled.div<{ $quote?: boolean; $dim?: boolean }>`
	position: relative;
	z-index: 2;
	margin: 20px 0 4px;
	padding: 0 16px 0 40px;
	width: fit-content;
	${({ $dim }) => $dim && 'opacity: 0.8;'}

	/* Caveat's quote glyph is tiny inside its em box, so the mark runs
	   well above the old Fjalla size — but stays inside the text gutter
	   so the glyph is never covered and always reads as a » “ « */
	&::before {
		content: '“';
		position: absolute;
		left: 2px;
		top: -20px;
		font-family: ${({ theme }) => theme.typography.fontHandwritten};
		font-size: 104px;
		line-height: 1;
		color: ${goldInk};
		opacity: 0.55;
		pointer-events: none;
	}

	.verse-text {
		margin: 0;
		/* verses keep a poem measure so authored <br> lines stay the only
		   breaks in practice; quotes have no authored breaklines, so they
		   run the full slide width — a short citat stays on one row */
		max-width: ${({ $quote }) => ($quote ? 'none' : '52ch')};
		font-style: italic;
		font-weight: ${({ $quote }) => ($quote ? 500 : 400)};
		color: ${({ theme }) => theme.palette.quiz.textMain};
		${({ $quote }) => ($quote ? questionScale : verseScale)}
		line-height: ${({ $quote }) => ($quote ? '120%' : '135%')};
	}

	${media.smallDesktop} {
		padding: 0 30px 0 64px;

		&::before {
			left: 6px;
			top: -30px;
			font-size: 160px;
		}
	}

	${QuestionContainer} && {
		padding-left: 24px;

		&::before {
			left: -6px;
		}
	}
`;

/* »Godina po događajima« clue list: each event led by the little
   Q-logo in gold — teal stays reserved for answers. */
export const EventTimeline = styled.div<{ $dim?: boolean }>`
	position: relative;
	z-index: 2;
	margin-top: 4px;
	padding: 0 16px;
	max-width: 680px;
	${({ $dim }) => $dim && 'opacity: 0.85;'}

	.ev {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 6px 0;
		font-weight: 500;
		color: ${({ theme }) => theme.palette.quiz.textMain};
		/* a step under the question, same as the etikete facts */
		${verseScale}
		line-height: 130%;
	}

	.ev svg {
		width: 0.75em;
		height: 0.75em;
		flex-shrink: 0;

		path {
			fill: ${goldInk};
		}
	}

	${media.smallDesktop} {
		padding: 0 30px;
		max-width: none;

		.ev {
			gap: 12px;
			padding: 7px 0;
		}
	}

	${QuestionContainer} && {
		padding: 0;
	}
`;

/* Handwritten gold Caveat: the anagram scramble ($scramble, big) and
   the rukopisni popis trace/ingredient list (default). Caveat runs
   optically small, so the list sits ~15% above the question ladder
   (founder, krug 7). $below marks a note rendered UNDER the question:
   it tucks up against the question text (whose own bottom margin is
   generous) and pushes the points row away. */
export const HandNote = styled.div<{
	$scramble?: boolean;
	$dim?: boolean;
	$below?: boolean;
}>`
	position: relative;
	z-index: 2;
	margin: ${({ $below }) => ($below ? '-14px 0 22px' : '10px 0 0')};
	padding: 0 16px;
	max-width: ${({ $scramble }) => ($scramble ? 'none' : '60ch')};
	width: fit-content;
	font-family: ${({ theme }) => theme.typography.fontHandwritten};
	font-weight: 600;
	color: ${goldInk};
	transform: rotate(-1.4deg);
	transform-origin: left bottom;
	${handScale}
	line-height: ${({ $scramble }) => ($scramble ? '110%' : '130%')};
	${({ $dim }) => $dim && 'opacity: 0.75;'}
	${({ $scramble }) => $scramble && 'font-size: 34px;'}

	${media.smallDesktop} {
		padding: 0 30px;
		/* pulls past the question container's + text's bottom margins so the
		   note reads as part of the question, with the points pushed away */
		margin: ${({ $below }) => ($below ? '-56px 0 34px' : '10px 0 0')};
		${({ $scramble }) => $scramble && 'font-size: clamp(48px, 4.6vw, 68px);'}
	}

	${QuestionContainer} && {
		padding: 0;
	}
`;

/* »Etikete« dossier grid (krug 7 GO: dosje-mreža) — gold Caveat
   labels right-aligned in their own column, facts in ink; on narrow
   screens it stacks label-above-value (the spis fallback from the
   proposal). Same language as the recipe mise-en-place split. */
export const FactGrid = styled.div<{ $dim?: boolean }>`
	position: relative;
	z-index: 2;
	text-align: left;
	margin: 10px 0 0;
	padding: 0 16px;
	${({ $dim }) => $dim && 'opacity: 0.85;'}

	.fact {
		padding: 5px 0;
	}

	.fl {
		font-family: ${({ theme }) => theme.typography.fontHandwritten};
		font-weight: 600;
		text-transform: lowercase;
		color: ${goldInk};
		${verseScale}
		line-height: 115%;
	}

	.fv {
		font-weight: 500;
		color: ${({ theme }) => theme.palette.quiz.textMain};
		${verseScale}
		line-height: 130%;
		max-width: 62ch;
	}

	${media.smallDesktop} {
		display: grid;
		grid-template-columns: max-content 1fr;
		column-gap: 24px;
		padding: 0 30px;

		.fact {
			display: contents;
		}

		.fl,
		.fv {
			padding: 6px 0;
		}

		.fl {
			justify-self: end;
			text-align: right;
			transform: rotate(-1.4deg);
			transform-origin: right bottom;
		}
	}

	${QuestionContainer} && {
		padding: 0;
	}
`;

/* Recept-kartica (locked: two columns on desktop — sastojci left,
   priprema right, question below in full width). Labels are gold
   Caveat handwriting; the content stays Ubuntu. */
export const RecipeCard = styled.div<{ $dim?: boolean }>`
	position: relative;
	z-index: 2;
	text-align: left;
	margin: 16px 0 0;
	padding: 0 16px;
	${({ $dim }) => $dim && 'opacity: 0.85;'}

	.rec-label {
		font-family: ${({ theme }) => theme.typography.fontHandwritten};
		font-weight: 600;
		font-size: 22px;
		line-height: 1;
		text-transform: lowercase;
		color: ${goldInk};
		display: inline-block;
		transform: rotate(-1.4deg);
		transform-origin: left bottom;
		margin: 0 0 8px;
	}

	/* recipe content runs ~30% below the question ladder, one uniform
	   weight throughout (founder, krug 7) */
	.rec-grid {
		display: grid;
		grid-template-columns: max-content 1fr;
		gap: 3px 18px;
		font-weight: 500;
		color: ${({ theme }) => theme.palette.quiz.textMain};
		${recipeScale}
	}

	.rec-grid .amt {
		color: ${({ theme }) => theme.palette.quiz.textMuted};
		text-align: right;
		font-variant-numeric: tabular-nums;
	}

	.rec-prep {
		margin: 0;
		max-width: 62ch;
		font-weight: 500;
		color: ${({ theme }) => theme.palette.quiz.textSoft};
		${recipeScale}
		line-height: 140%;
	}

	.rec-col + .rec-col {
		margin-top: 14px;
	}

	${media.smallDesktop} {
		padding: 0 30px;
		display: grid;
		grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
		gap: 0 56px;
		align-items: start;

		.rec-col + .rec-col {
			margin-top: 0;
		}

		/* ingredients-only recipes: the lone column spans the grid,
		   left-aligned (founder, 2026-08-27) */
		.rec-col:only-child {
			grid-column: 1 / -1;
		}

		.rec-label {
			font-size: clamp(34px, 2.6vw, 44px);
		}
	}

	${QuestionContainer} && {
		padding: 0;
	}
`;

/* »Napomena« author instruction: gold Caveat label + the note in muted
   italic — the author's voice, visually apart from the question. */
export const AuthorNote = styled.div<{ $dim?: boolean }>`
	position: relative;
	z-index: 2;
	display: flex;
	align-items: baseline;
	gap: 10px;
	margin-top: 12px;
	padding: 0 16px;
	width: 100%;
	text-align: left;
	${({ $dim }) => $dim && 'opacity: 0.8;'}

	.n-label {
		font-family: ${({ theme }) => theme.typography.fontHandwritten};
		font-weight: 600;
		font-size: 26px;
		line-height: 1;
		text-transform: lowercase;
		color: ${goldInk};
		display: inline-block;
		transform: rotate(-1.4deg);
		flex-shrink: 0;
	}

	/* krug 7 (founder): the note reads upright, in the question's ink,
	   a step larger — only the gold label marks it as the author's voice */
	.n-text {
		font-size: 15px;
		line-height: 1.5;
		font-weight: 500;
		color: ${({ theme }) => theme.palette.quiz.textMain};
	}

	${media.smallDesktop} {
		padding: 0 30px;

		.n-label {
			font-size: 37px;
		}

		.n-text {
			font-size: clamp(17px, 1.35vw, 22px);
		}
	}

	${QuestionContainer} && {
		padding: 0;
	}
`;
